import { useFormik } from 'formik';
import * as Yup from 'yup';

import {
  adminActions,
  adminUpdateWorkshiftRateAllowances,
  AdminAllowanceNames,
  AllowanceEnums,
  AllowancesNames,
  AllowanceTypesToHandle,
  isLoadingSelector,
  TravelAllowanceEnums,
} from '@pharmaplan/common';
import type {
  IAdminUpdateWorkshiftRateAllowancesModel,
} from '@pharmaplan/common';

import {
  commonAllowanceValidations,
  hourlyRateValidator,
} from '../../../helpers/commonValidators';
import strings from '../../../localization';
import { resetDialog } from '../../../reducers/dialogReducer';
import { useAppDispatch } from '../../useAppDispatch';
import { useAppSelector } from '../../useAppSelector';

type AllowanceInput = number | string;

export type IUpdateWorkshiftRateAllowancesFormValues = Record<
  AllowanceEnums,
  boolean
> & {
  accommodationAllowance: AllowanceInput;
  accommodationAllowanceType: AllowanceInput;
  emergencyFees: AllowanceInput;
  mealAllowance: AllowanceInput;
  notes: string;
  otherAllowance: AllowanceInput;
  paidHourlyRate: AllowanceInput;
  pharmacyHourlyRate: AllowanceInput;
  travelAllowanceAmount: AllowanceInput;
  travelAllowanceRatePerKm: AllowanceInput;
  travelAllowanceTypes: Array<AllowanceInput>;
};

const {
  paidHourlyRate,
} = AllowancesNames;
const { pharmacyHourlyRate } = AdminAllowanceNames;
const { accommodation, emergency, meal, others, travel } = AllowanceEnums;

const initialValues: IUpdateWorkshiftRateAllowancesFormValues = {
  [accommodation]: false,
  [emergency]: false,
  [meal]: false,
  [others]: false,
  [travel]: false,
  accommodationAllowance: '',
  accommodationAllowanceType: '',
  emergencyFees: '',
  mealAllowance: '',
  notes: '',
  otherAllowance: '',
  paidHourlyRate: '',
  pharmacyHourlyRate: '',
  travelAllowanceAmount: '',
  travelAllowanceRatePerKm: '',
  travelAllowanceTypes: [],
};

const validationSchema = Yup.object().shape({
  ...commonAllowanceValidations,
  [paidHourlyRate]: hourlyRateValidator().lessThan(
    Yup.ref(pharmacyHourlyRate),
    strings.cannotBeMoreThanBill,
  ),
  [pharmacyHourlyRate]: hourlyRateValidator(),
});

const parseNullableNumber = (value: AllowanceInput) =>
  (value === '' ? null : Number.parseFloat(`${value}`));

const parseEnabledAllowance = (
  isEnabled: boolean,
  value: AllowanceInput,
) =>
  (isEnabled ? parseNullableNumber(value) : null);

const hasTravelAllowanceType = (
  allowanceTypes: Array<AllowanceInput>,
  type: TravelAllowanceEnums,
) =>
  allowanceTypes.some((allowanceType) =>
    `${allowanceType}` === type);

const createUpdateModel = (
  values: IUpdateWorkshiftRateAllowancesFormValues,
): IAdminUpdateWorkshiftRateAllowancesModel => {
  const travelAllowanceTypes = values[travel]
    ? values.travelAllowanceTypes.map((allowanceType) =>
      Number(allowanceType))
    : [];
  const isAccommodationAmountEnabled = values[accommodation]
    && `${values.accommodationAllowanceType}`
      === AllowanceTypesToHandle.paidValuePerNight;

  return {
    paidHourlyRate: Number.parseFloat(`${values.paidHourlyRate}`),
    pharmacyHourlyRate: Number.parseFloat(`${values.pharmacyHourlyRate}`),
    travelAllowanceTypes,
    travelAllowanceRatePerKm: parseEnabledAllowance(
      values[travel]
        && hasTravelAllowanceType(
          values.travelAllowanceTypes,
          TravelAllowanceEnums.valueKm,
        ),
      values.travelAllowanceRatePerKm,
    ),
    travelAllowanceAmount: parseEnabledAllowance(
      values[travel]
        && hasTravelAllowanceType(
          values.travelAllowanceTypes,
          TravelAllowanceEnums.travelAmount,
        ),
      values.travelAllowanceAmount,
    ),
    accommodationAllowanceType: values[accommodation]
      ? Number(values.accommodationAllowanceType)
      : null,
    accommodationAllowance: parseEnabledAllowance(
      isAccommodationAmountEnabled,
      values.accommodationAllowance,
    ),
    mealAllowance: parseEnabledAllowance(
      values[meal],
      values.mealAllowance,
    ),
    emergencyFees: parseEnabledAllowance(
      values[emergency],
      values.emergencyFees,
    ),
    otherAllowance: parseEnabledAllowance(
      values[others],
      values.otherAllowance,
    ),
    note: values.notes,
  };
};

const useUpdateWorkshiftRateAllowances = (shiftIds: Array<string>) => {
  const dispatch = useAppDispatch();
  const isSubmitting = useAppSelector((state) =>
    isLoadingSelector([adminActions.updateWorkshiftRateAllowances], state));

  const formik = useFormik<IUpdateWorkshiftRateAllowancesFormValues>({
    initialValues,
    validationSchema,
    onSubmit: (values) => {
      dispatch(
        adminUpdateWorkshiftRateAllowances({
          shiftIds,
          model: createUpdateModel(values),
        }),
      );
    },
  });

  const handleClose = () => {
    dispatch(resetDialog());
  };

  return {
    formik,
    handleClose,
    isSubmitting,
  };
};

export default useUpdateWorkshiftRateAllowances;
